import { DataTableDesignTokens } from '@primeng/themes/types/datatable';

export interface ICustomDataTableDesignTokens extends DataTableDesignTokens {
  row: DataTableDesignTokens['row'] & {
    selectedBorder: {
      color: string;
      radius: string;
    };
  };
  sortIcon: DataTableDesignTokens['sortIcon'] & {
    size: string;
  };
  columnResizerHoverBg?: string;
}

export const baseDatatableToken: ICustomDataTableDesignTokens = {
  header: {
    borderWidth: '0',
    padding: '0',
  },
  headerCell: {
    hoverBackground: 'transparent',
    selectedBackground: 'transparent',
    borderColor: 'transparent',
    color: '{gray.600}',
    hoverColor: '{gray.600}',
    selectedColor: '{gray.600}',
    padding: '0.125rem 1rem 0.125rem 0.125rem',
  },
  columnTitle: {
    fontWeight: '500',
  },
  row: {
    background: '{content.background}',
    hoverBackground: '{neutral.500}',
    selectedBackground: '{neutral.500}',
    color: '{gray.600}',
    hoverColor: '{gray.600}',
    selectedColor: '{gray.600}',
    selectedBorder: {
      color: '{primary.500}',
      radius: '0.25rem',
    },
  },
  bodyCell: {
    borderColor: 'transparent',
    padding: '0.125rem 1rem 0.125rem 0.125rem',
  },
  sortIcon: {
    size: '1.25rem',
  },
  colorScheme: {
    light: {
      row: {
        stripedBackground: '{other.gray.50}',
      },
      bodyCell: {
        selectedBorderColor: 'transparent',
      },
    },
  },
  columnResizerHoverBg: '{neutral.500}',
  // it can be as default settings, but to not break the original design it was moved to additional classes
  // should be checked in the future tables
  //  fix-scroll-for-selected-row: header jump, selected row border not visible
  //  table-row-hover-marker: add marker for hovered row
  //  table-virtual-scroll-height-full: by default scroll on full height (horizontal scroll not after last row)
  //  fix-flex-scroll-height: flex height for scrolled table limited by parent max-height
  css: ({ dt }: { dt: (token: string, val?: string) => string }) => `
    p-table.table-virtual-scroll-height-full .p-virtualscroller {
      height: 100% !important;
    }

    p-table.fix-flex-scroll-height {
      display: flex;
      flex-direction: column;
      height: 100%;
      min-height: 0;
    }

    p-table.fix-flex-scroll-height .p-datatable {
      min-height: 0;
    }

    p-table.fix-scroll-for-selected-row .p-datatable-table {
      contain: layout paint;
    }

    p-table.fix-scroll-for-selected-row .p-datatable-table {
      padding-bottom: 1px;
      padding-right: 1px;
    }

    p-table.fix-scroll-for-selected-row .p-datatable-table .p-datatable-thead > tr th {
      border-width: 0;
    }

    p-table.fix-scroll-for-selected-row .p-datatable-table .p-datatable-tbody > tr td {
      border-width: 0;
    }

    p-table .p-datatable-table .p-datatable-tbody > tr.p-datatable-row-selected td:first-child {
      border-top-left-radius: ${dt('datatable.row.selectedBorder.radius')};
      border-bottom-left-radius: ${dt('datatable.row.selectedBorder.radius')};
      border-left: 1px solid ${dt('datatable.row.selectedBorder.color')};
    }

    p-table .p-datatable-table .p-datatable-tbody > tr.p-datatable-row-selected td:last-child {
      border-top-right-radius: ${dt('datatable.row.selectedBorder.radius')};
      border-bottom-right-radius: ${dt('datatable.row.selectedBorder.radius')};
      border-right: 1px solid ${dt('datatable.row.selectedBorder.color')};
    }

    p-table .p-datatable-table .p-datatable-tbody > tr.p-datatable-row-selected td {
      border-top: 1px solid ${dt('datatable.row.selectedBorder.color')};
      border-bottom: 1px solid ${dt('datatable.row.selectedBorder.color')};
    }

    p-table.table-row-hover-marker .p-datatable-striped.p-datatable-hoverable .p-datatable-tbody > tr:not(.p-datatable-row-selected):hover .hover-marker{
      display: inline-block !important;
    }

    p-table.table-row-hover-marker .p-datatable.p-datatable-striped .p-datatable-tbody > tr {
      background-color: transparent !important;
    }

    p-table.table-row-hover-marker .p-datatable.p-datatable-striped .p-datatable-tbody > tr > td {
      background-color: ${dt('datatable.row.background')};
    }

    p-table.table-row-hover-marker .p-datatable.p-datatable-striped .p-datatable-tbody > tr:nth-child(odd) > td {
      background-color: ${dt('datatable.row.stripedBackground')};
    }

    p-table.table-row-hover-marker .p-datatable-striped.p-datatable-hoverable .p-datatable-tbody > tr:not(.p-datatable-row-selected):hover > td {
      background-color: ${dt('datatable.row.hoverBackground')} !important;
    }

    p-table .p-datatable-column-resizer:hover {
      background-color: ${dt('datatable.columnResizerHoverBg')};
    }
  `,
};
